import * as ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import './config/i18n'

import ErrorBoundary from './components/organisms/ErrorBoundary'
import { AuthProvider } from './hooks/useAuth'
import { BrandingProvider } from './hooks/useBranding'
import { applyBrandVars, DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from './lib/branding-shades'
import { AppRoutes } from './routes'
import { loadAuthConfig } from './services/auth/config'

import './styles/global.css'

// Seed the `--brand-*` CSS variables before the first render so every page —
// including pre-login pages that never mount AppLayout — is styled. AppLayout
// re-applies these with the tenant's colours once the user is authenticated.
applyBrandVars(DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR)

// Plan 4 — fetch /config.json BEFORE mounting React. The auth client factory
// (Task 7) dispatches on AuthConfig.provider, so callers in useAuth + the auth
// pages depend on the config being loaded by the time their effects run.
// Customer ops swaps /config.json at deploy time to flip the SPA between
// Cognito (SaaS) and Keycloak (on-prem).
async function boot(): Promise<void> {
  try {
    await loadAuthConfig()

    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

    root.render(
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <BrandingProvider>
              <AppRoutes />
            </BrandingProvider>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>,
    )
  } catch (err) {
    // Configuration error UI — customer's most likely first-day failure mode on-prem
    // is a misconfigured /config.json. Blank screen with only a console error is hostile.
    // Inline-style is intentional — Tailwind classes aren't loaded yet at this stage
    // (the App hasn't mounted), so we can't rely on the design system.
    const message = err instanceof Error ? err.message : String(err)
    const rootEl = document.getElementById('root')

    if (rootEl) {
      const escaped = message.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c] ?? c)
      rootEl.innerHTML = `
        <div style="font-family:system-ui;max-width:560px;margin:80px auto;padding:24px;color:#7a1212;background:#fef2f2;border:1px solid #fca5a5;border-radius:8px">
          <h1 style="margin:0 0 8px;font-size:18px">Configuration error</h1>
          <p style="margin:0 0 12px;font-size:14px">The SPA failed to load <code>/config.json</code>. Check your deployment and reload.</p>
          <pre style="margin:0;padding:12px;background:#fff;border-radius:4px;border:1px solid #fecaca;font-size:12px;overflow:auto;white-space:pre-wrap">${escaped}</pre>
        </div>
      `
    }

    // eslint-disable-next-line no-console
    console.error('[boot] /config.json load failed', err)
  }
}

void boot()
